import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import { SwipeMatchLogo } from '../components/SwipeMatchLogo';
import api from '../services/api';
import { 
  ArrowLeft, Save, Loader2, MapPin, Euro, Ruler, Calendar,
  X, Upload, ImagePlus, Sofa, Home, Dog, FileCheck, Users, Clock, Search
} from 'lucide-react';

const CITIES = [
  'Amsterdam', 'Rotterdam', 'Utrecht', 'Den Haag', 
  'Eindhoven', 'Groningen', 'Tilburg', 'Almere'
];

const PROPERTY_TYPES = [
  { id: 'kamer', label: 'Kamer' },
  { id: 'studio', label: 'Studio' },
  { id: 'appartement', label: 'Appartement' },
  { id: 'eengezinswoning', label: 'Eengezinswoning' },
];

const RENTAL_DURATIONS = [
  { id: 'short_1_3', label: '1-3 maanden' },
  { id: 'medium_3_6', label: '3-6 maanden' },
  { id: 'medium_6_12', label: '6-12 maanden' },
  { id: 'long_1_year', label: '1 jaar+' },
  { id: 'any', label: 'Flexibel' },
];

const EditListingPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [listing, setListing] = useState({
    title: '',
    type: 'kamer',
    city: '',
    neighborhood: '',
    postalCode: '',
    streetAddress: '',
    price: '',
    size: '',
    available: '',
    availableUntil: 'indefinite',
    furnished: false,
    description: '',
    images: [],
    // Additional preferences
    maxPersons: 2,
    petsAllowed: null,
    registrationAllowed: null,
    minRentalDuration: 'any',
    // NEW: Utility and service costs
    utilitiesIncluded: null,
    utilitiesCost: '',
    serviceCost: '',
    // NEW: Property features
    propertyFeatures: {}
  });
  
  // Property features state for editing
  const [propertyFeatures, setPropertyFeatures] = useState({
    balcony: false, balconySize: '', garden: false, gardenSize: '', storage: false,
    kitchen: '', shower: '', toilet: '', livingroom: '',
    energyLabel: '', solarPanels: false,
    elevator: false, washingConnection: false, dryerConnection: false, dishwasher: false,
    kitchenType: '', internetIncluded: false, smokingAllowed: false,
    bathtub: false, showerBathroom: false, sink: false, toiletInBathroom: false,
    parkingIncluded: false, parkingAvailable: false, parkingPermitNeeded: false, noParking: false,
    publicTransport: false, supermarketNearby: false, schoolNearby: false, neighborhoodType: '',
    airco: false, smartHome: false, alarmSystem: false, videoIntercom: false, bikeStorage: false,
  });
  
  // Address autocomplete state
  const [addressQuery, setAddressQuery] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [loadingAddress, setLoadingAddress] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    // Only landlords can edit listings
    if (user?.userType === 'huurder') {
      navigate('/dashboard');
      return;
    }
    loadListing();
  }, [id, isAuthenticated, authLoading, navigate, user]);

  const loadListing = async () => {
    try {
      const res = await api.get(`/listings/${id}`);
      if (res.data) {
        setListing({
          title: res.data.title || '',
          type: res.data.type || 'kamer',
          city: res.data.city || '',
          neighborhood: res.data.neighborhood || '',
          postalCode: res.data.postalCode || '',
          streetAddress: res.data.streetAddress || '',
          price: res.data.price?.toString() || '',
          size: res.data.size?.toString() || '',
          available: res.data.available || '',
          availableUntil: res.data.availableUntil || 'indefinite',
          furnished: res.data.furnished || false,
          description: res.data.description || '',
          images: res.data.images || [],
          // Additional preferences
          maxPersons: res.data.maxPersons || 2,
          petsAllowed: res.data.petsAllowed ?? null,
          registrationAllowed: res.data.registrationAllowed ?? null,
          minRentalDuration: res.data.minRentalDuration || 'any',
          // NEW: Utility and service costs
          utilitiesIncluded: res.data.utilitiesIncluded ?? null,
          utilitiesCost: res.data.utilitiesCost?.toString() || '',
          serviceCost: res.data.serviceCost?.toString() || '',
          // Property features
          propertyFeatures: res.data.propertyFeatures || {}
        });
        // Load property features into separate state
        if (res.data.propertyFeatures) {
          setPropertyFeatures(prev => ({ ...prev, ...res.data.propertyFeatures }));
        }
        // Set address query for display
        if (res.data.streetAddress && res.data.postalCode) {
          setAddressQuery(`${res.data.streetAddress}, ${res.data.postalCode}`);
        }
      }
    } catch (error) {
      toast({ title: 'Woning niet gevonden', variant: 'destructive' });
      navigate('/verhuurder/dashboard');
    } finally {
      setLoading(false);
    }
  };
  
  // Address autocomplete using PDOK Locatieserver (Dutch government API)
  const searchAddress = async (query) => {
    if (query.length < 3) {
      setAddressSuggestions([]);
      return;
    }
    
    setLoadingAddress(true);
    try {
      const response = await fetch(
        `https://api.pdok.nl/bzk/locatieserver/search/v3_1/suggest?q=${encodeURIComponent(query)}&fq=type:adres&rows=5`
      );
      const data = await response.json();
      
      if (data.response?.docs) {
        const suggestions = data.response.docs.map(doc => {
          // Extract city and street from weergavenaam (format: "Straatnaam 123, 1234AB Stad")
          const displayName = doc.weergavenaam || '';
          const parts = displayName.split(',');
          
          let extractedCity = '';
          let extractedStreet = '';
          let extractedPostcode = '';
          
          if (parts.length >= 2) {
            extractedStreet = parts[0].trim();
            const lastPart = parts[parts.length - 1].trim();
            const lastPartWords = lastPart.split(' ');
            
            if (lastPartWords.length > 0 && /\d/.test(lastPartWords[0])) {
              extractedPostcode = lastPartWords[0];
              extractedCity = lastPartWords.slice(1).join(' ');
            } else {
              extractedCity = lastPart;
            }
          }
          
          return {
            id: doc.id,
            display: displayName,
            postcode: doc.postcode || extractedPostcode || '',
            city: doc.woonplaatsnaam || extractedCity || '',
            street: doc.straatnaam || extractedStreet || '',
            houseNumber: doc.huisnummer || '',
            neighborhood: doc.wijknaam || doc.buurtnaam || ''
          };
        });
        setAddressSuggestions(suggestions);
      }
    } catch (error) {
      console.error('Address search error:', error);
      setAddressSuggestions([]);
    } finally {
      setLoadingAddress(false);
    }
  };
  
  const selectAddress = (suggestion) => {
    setListing(prev => ({
      ...prev,
      postalCode: suggestion.postcode || '',
      city: suggestion.city || prev.city,
      neighborhood: suggestion.neighborhood || suggestion.street || '',
      streetAddress: suggestion.street ? `${suggestion.street} ${suggestion.houseNumber || ''}`.trim() : ''
    }));
    setAddressQuery(suggestion.display);
    setAddressSuggestions([]);
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploading(true);
    
    for (const file of files) {
      if (!file.type.startsWith('image/')) continue;
      if (file.size > 4 * 1024 * 1024) continue;

      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await api.post('/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        setListing(prev => ({ ...prev, images: [...prev.images, response.data.url] }));
      } catch (error) {
        console.error('Upload error:', error);
      }
    }
    
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImage = (index) => {
    setListing(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
  };

  const getImageUrl = (img) => {
    if (!img) return '';
    if (img.startsWith('http')) return img;
    return `${API_URL}${img}`;
  };

  const handleSave = async () => {
    if (!listing.title || !listing.price || !listing.city || !listing.size || !listing.available) {
      toast({ title: 'Vul alle verplichte velden in', description: 'Titel, prijs, stad, oppervlakte en beschikbaarheidsdatum zijn verplicht.', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      await api.patch(`/listings/${id}`, {
        title: listing.title,
        type: listing.type,
        city: listing.city,
        neighborhood: listing.neighborhood,
        postalCode: listing.postalCode,
        streetAddress: listing.streetAddress,
        price: parseFloat(listing.price),
        size: listing.size ? parseFloat(listing.size) : 0,
        available: listing.available,
        availableUntil: listing.availableUntil,
        furnished: listing.furnished,
        description: listing.description,
        images: listing.images,
        // Additional preferences
        maxPersons: listing.maxPersons,
        petsAllowed: listing.petsAllowed,
        registrationAllowed: listing.registrationAllowed,
        minRentalDuration: listing.minRentalDuration,
        // NEW: Utility and service costs
        utilitiesIncluded: listing.utilitiesIncluded,
        utilitiesCost: listing.utilitiesCost ? parseFloat(listing.utilitiesCost) : null,
        serviceCost: listing.serviceCost ? parseFloat(listing.serviceCost) : null,
        // NEW: Property features
        propertyFeatures: propertyFeatures
      });
      
      toast({ title: 'Woning opgeslagen!' });
      navigate('/verhuurder/dashboard');
    } catch (error) {
      toast({ title: 'Er ging iets mis', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="fixed inset-0 bg-[#F7F7F7] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#2E7D32] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F7F7]" data-testid="edit-listing-page">
      <Toaster />

      {/* Header */}
      <div className="bg-white border-b border-[#E5E5E5] sticky top-0 z-10">
        <div className="max-w-xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate(-1)}>
            <ArrowLeft className="w-6 h-6 text-[#2A2A2A]" />
          </button>
          <SwipeMatchLogo size="small" variant="color" />
          <div className="w-6" />
        </div>
      </div>

      {/* Content */}
      <div className="max-w-xl mx-auto px-4 py-6 pb-32">
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-black text-[#2A2A2A] mb-2">Woning bewerken</h1>
          <p className="text-[#9E9E9E]">Pas je advertentie aan</p>
        </div>

        {/* Images */}
        <div className="mb-6">
          <label className="text-sm font-bold text-[#2A2A2A] mb-3 block">Foto's</label>
          
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImageUpload}
            accept="image/*"
            multiple
            className="hidden"
          />
          
          <div className="grid grid-cols-3 gap-3">
            {listing.images.map((img, i) => (
              <div key={i} className="relative aspect-square">
                <img 
                  src={getImageUrl(img)}
                  alt={`Foto ${i + 1}`}
                  className="w-full h-full object-cover rounded-xl"
                />
                <button
                  onClick={() => removeImage(i)}
                  className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
                >
                  <X className="w-3 h-3" />
                </button>
                {i === 0 && (
                  <span className="absolute bottom-2 left-2 bg-[#2E7D32] text-white text-[10px] px-2 py-0.5 rounded font-medium">
                    Hoofd
                  </span>
                )}
              </div>
            ))}
            
            {listing.images.length < 6 && (
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="aspect-square rounded-xl border-2 border-dashed border-[#E5E5E5] flex flex-col items-center justify-center gap-2 hover:border-[#2E7D32] transition-colors bg-white"
              >
                {uploading ? (
                  <Loader2 className="w-6 h-6 text-[#9E9E9E] animate-spin" />
                ) : (
                  <>
                    <Upload className="w-6 h-6 text-[#9E9E9E]" />
                    <span className="text-xs text-[#9E9E9E]">Toevoegen</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Title */}
        <div className="mb-6">
          <label className="text-sm font-bold text-[#2A2A2A] mb-2 block">Titel *</label>
          <Input
            value={listing.title}
            onChange={(e) => setListing(prev => ({ ...prev, title: e.target.value }))}
            placeholder="bijv. Ruime kamer in gezellig studentenhuis"
            className="h-12 rounded-xl border-[#E5E5E5]"
          />
        </div>

        {/* Type */}
        <div className="mb-6">
          <label className="text-sm font-bold text-[#2A2A2A] mb-2 block">Type</label>
          <div className="grid grid-cols-2 gap-2">
            {PROPERTY_TYPES.map((type) => (
              <button
                key={type.id}
                onClick={() => setListing(prev => ({ ...prev, type: type.id }))}
                className={`p-3 rounded-xl border-2 text-center transition-all ${
                  listing.type === type.id
                    ? 'border-[#2E7D32] bg-[#2E7D32]/5'
                    : 'border-[#E5E5E5] bg-white'
                }`}
              >
                <span className={`font-medium text-sm ${
                  listing.type === type.id ? 'text-[#2E7D32]' : 'text-[#2A2A2A]'
                }`}>
                  {type.label}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Address Autocomplete */}
        <div className="mb-6">
          <label className="text-sm font-bold text-[#2A2A2A] mb-2 block flex items-center gap-2">
            <Search className="w-4 h-4 text-[#2E7D32]" />
            Adres zoeken
          </label>
          <div className="relative">
            <Input
              value={addressQuery}
              onChange={(e) => {
                setAddressQuery(e.target.value);
                searchAddress(e.target.value);
              }}
              placeholder="Typ postcode of adres..."
              className="h-12 rounded-xl border-[#E5E5E5] pr-10"
            />
            {loadingAddress && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9E9E9E] animate-spin" />
            )}
          </div>
          {addressSuggestions.length > 0 && (
            <div className="mt-2 bg-white border border-[#E5E5E5] rounded-xl shadow-lg overflow-hidden">
              {addressSuggestions.map((suggestion) => (
                <button
                  key={suggestion.id}
                  onClick={() => selectAddress(suggestion)}
                  className="w-full px-4 py-3 text-left hover:bg-[#F7F7F7] border-b border-[#E5E5E5] last:border-b-0 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <MapPin className="w-4 h-4 text-[#2E7D32] mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-[#2A2A2A]">{suggestion.display}</p>
                      {suggestion.postcode && (
                        <p className="text-xs text-[#9E9E9E]">{suggestion.postcode} {suggestion.city}</p>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
          <p className="text-xs text-[#9E9E9E] mt-2">Typ een postcode of straatnaam met huisnummer</p>
        </div>

        {/* City */}
        <div className="mb-6">
          <label className="text-sm font-bold text-[#2A2A2A] mb-2 block flex items-center gap-2">
            <MapPin className="w-4 h-4 text-[#2E7D32]" />
            Stad *
          </label>
          <div className="grid grid-cols-2 gap-2">
            {CITIES.map((city) => (
              <button
                key={city}
                onClick={() => setListing(prev => ({ ...prev, city }))}
                className={`p-3 rounded-xl border-2 text-left transition-all ${
                  listing.city === city
                    ? 'border-[#2E7D32] bg-[#2E7D32]/5'
                    : 'border-[#E5E5E5] bg-white'
                }`}
              >
                <span className={`font-medium ${
                  listing.city === city ? 'text-[#2E7D32]' : 'text-[#2A2A2A]'
                }`}>
                  {city}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Neighborhood */}
        <div className="mb-6">
          <label className="text-sm font-bold text-[#2A2A2A] mb-2 block">Buurt</label>
          <Input
            value={listing.neighborhood}
            onChange={(e) => setListing(prev => ({ ...prev, neighborhood: e.target.value }))}
            placeholder="bijv. De Pijp, Centrum"
            className="h-12 rounded-xl border-[#E5E5E5]"
          />
        </div>

        {/* Price & Size */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div>
            <label className="text-sm font-bold text-[#2A2A2A] mb-2 block flex items-center gap-2">
              <Euro className="w-4 h-4 text-[#2E7D32]" />
              Prijs/mnd *
            </label>
            <Input
              type="number"
              value={listing.price}
              onChange={(e) => setListing(prev => ({ ...prev, price: e.target.value }))}
              placeholder="750"
              className="h-12 rounded-xl border-[#E5E5E5]"
              required
            />
          </div>
          <div>
            <label className="text-sm font-bold text-[#2A2A2A] mb-2 block flex items-center gap-2">
              <Ruler className="w-4 h-4 text-[#2E7D32]" />
              Oppervlakte (m²) *
            </label>
            <Input
              type="number"
              value={listing.size}
              onChange={(e) => setListing(prev => ({ ...prev, size: e.target.value }))}
              placeholder="25"
              className="h-12 rounded-xl border-[#E5E5E5]"
              required
            />
          </div>
        </div>

        {/* Available & Furnished */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div>
            <label className="text-sm font-bold text-[#2A2A2A] mb-2 block flex items-center gap-2">
              <Calendar className="w-4 h-4 text-[#2E7D32]" />
              Beschikbaar vanaf *
            </label>
            <Input
              type="date"
              value={listing.available}
              onChange={(e) => setListing(prev => ({ ...prev, available: e.target.value }))}
              className="h-12 rounded-xl border-[#E5E5E5]"
              required
            />
          </div>
          <div>
            <label className="text-sm font-bold text-[#2A2A2A] mb-2 block flex items-center gap-2">
              <Calendar className="w-4 h-4 text-[#9C27B0]" />
              Beschikbaar tot *
            </label>
            <div className="space-y-2">
              <button
                onClick={() => setListing(prev => ({ ...prev, availableUntil: 'indefinite' }))}
                className={`w-full p-2.5 rounded-xl border-2 text-left transition-all flex items-center gap-2 text-sm ${
                  listing.availableUntil === 'indefinite'
                    ? 'border-[#9C27B0] bg-[#9C27B0]/5'
                    : 'border-[#E5E5E5] hover:border-[#9E9E9E]'
                }`}
              >
                <span>♾️</span>
                <span className="font-medium text-[#2A2A2A]">Onbepaald</span>
              </button>
              <Input
                type="date"
                className={`h-10 rounded-xl border-[#E5E5E5] text-sm ${listing.availableUntil === 'indefinite' ? 'opacity-50' : ''}`}
                value={listing.availableUntil === 'indefinite' ? '' : (listing.availableUntil || '')}
                onChange={(e) => setListing(prev => ({ ...prev, availableUntil: e.target.value }))}
              />
            </div>
          </div>
        </div>

        {/* GWL - Utilities Section */}
        <div className="mb-6">
          <label className="text-sm font-bold text-[#2A2A2A] mb-2 block flex items-center gap-2">
            <Euro className="w-4 h-4 text-[#FF9800]" />
            Huurprijs inclusief of exclusief GWL?
          </label>
          <p className="text-xs text-[#9E9E9E] mb-3">GWL = Gas, Water en Licht</p>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setListing(prev => ({ ...prev, utilitiesIncluded: true, utilitiesCost: '' }))}
              className={`p-3 rounded-xl border-2 text-center transition-all ${
                listing.utilitiesIncluded === true
                  ? 'border-[#2E7D32] bg-[#2E7D32]/5'
                  : 'border-[#E5E5E5] bg-white hover:border-[#2E7D32]/50'
              }`}
            >
              <span className="text-xl mb-1 block">✅</span>
              <span className="font-medium text-sm text-[#2A2A2A]">Inclusief GWL</span>
            </button>
            <button
              onClick={() => setListing(prev => ({ ...prev, utilitiesIncluded: false }))}
              className={`p-3 rounded-xl border-2 text-center transition-all ${
                listing.utilitiesIncluded === false
                  ? 'border-[#FF9800] bg-[#FF9800]/5'
                  : 'border-[#E5E5E5] bg-white hover:border-[#FF9800]/50'
              }`}
            >
              <span className="text-xl mb-1 block">➕</span>
              <span className="font-medium text-sm text-[#2A2A2A]">Exclusief GWL</span>
            </button>
          </div>
        </div>

        {/* GWL Cost - Only show if excluded */}
        {listing.utilitiesIncluded === false && (
          <div className="mb-6 p-4 bg-[#FF9800]/10 rounded-xl border border-[#FF9800]/30">
            <label className="text-sm font-bold text-[#2A2A2A] mb-2 block">
              Geschatte GWL kosten per maand (optioneel)
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9E9E9E] font-bold">€</span>
              <Input
                type="number"
                placeholder="100"
                className="h-12 rounded-xl border-[#E5E5E5] pl-10"
                value={listing.utilitiesCost}
                onChange={(e) => setListing(prev => ({ ...prev, utilitiesCost: e.target.value }))}
              />
            </div>
          </div>
        )}

        {/* Service Costs - Optional */}
        <div className="mb-6">
          <label className="text-sm font-bold text-[#2A2A2A] mb-2 block flex items-center gap-2">
            <Euro className="w-4 h-4 text-[#9E9E9E]" />
            Extra servicekosten per maand (optioneel)
          </label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9E9E9E] font-bold">€</span>
            <Input
              type="number"
              placeholder="0"
              className="h-12 rounded-xl border-[#E5E5E5] pl-10"
              value={listing.serviceCost}
              onChange={(e) => setListing(prev => ({ ...prev, serviceCost: e.target.value }))}
            />
          </div>
          <p className="text-xs text-[#9E9E9E] mt-1">Bijv. schoonmaak, internet, etc.</p>
        </div>

        {/* Furnished */}
        <div className="mb-6">
          <label className="text-sm font-bold text-[#2A2A2A] mb-2 block flex items-center gap-2">
            <Sofa className="w-4 h-4 text-[#2E7D32]" />
            Gemeubileerd
          </label>
          <button
            onClick={() => setListing(prev => ({ ...prev, furnished: !prev.furnished }))}
            className={`w-full h-12 rounded-xl border-2 font-medium transition-all ${
              listing.furnished
                ? 'border-[#2E7D32] bg-[#2E7D32]/5 text-[#2E7D32]'
                : 'border-[#E5E5E5] bg-white text-[#2A2A2A]'
            }`}
          >
            {listing.furnished ? 'Ja' : 'Nee'}
          </button>
        </div>

        {/* Description */}
        <div className="mb-6">
          <label className="text-sm font-bold text-[#2A2A2A] mb-2 block">Beschrijving</label>
          <Textarea
            value={listing.description}
            onChange={(e) => setListing(prev => ({ ...prev, description: e.target.value }))}
            placeholder="Beschrijf je woning..."
            className="min-h-[150px] rounded-xl border-[#E5E5E5]"
          />
        </div>

        {/* House Rules Section */}
        <div className="mb-6 p-4 bg-[#F7F7F7] rounded-2xl">
          <h3 className="text-lg font-bold text-[#2A2A2A] mb-4 flex items-center gap-2">
            <FileCheck className="w-5 h-5 text-[#1F3A5F]" />
            Huisregels
          </h3>

          {/* Max Persons */}
          <div className="mb-4">
            <label className="text-sm font-bold text-[#2A2A2A] mb-2 block flex items-center gap-2">
              <Users className="w-4 h-4 text-[#2E7D32]" />
              Maximum aantal personen
            </label>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setListing(prev => ({ ...prev, maxPersons: Math.max(1, prev.maxPersons - 1) }))}
                className="w-10 h-10 rounded-lg bg-white border border-[#E5E5E5] flex items-center justify-center text-[#2A2A2A] font-bold hover:border-[#2E7D32] transition-colors"
              >
                -
              </button>
              <span className="text-2xl font-black text-[#2A2A2A] w-12 text-center">{listing.maxPersons}</span>
              <button
                onClick={() => setListing(prev => ({ ...prev, maxPersons: Math.min(10, prev.maxPersons + 1) }))}
                className="w-10 h-10 rounded-lg bg-white border border-[#E5E5E5] flex items-center justify-center text-[#2A2A2A] font-bold hover:border-[#2E7D32] transition-colors"
              >
                +
              </button>
              <span className="text-sm text-[#9E9E9E]">{listing.maxPersons === 1 ? 'persoon' : 'personen'}</span>
            </div>
          </div>

          {/* Pets Allowed */}
          <div className="mb-4">
            <label className="text-sm font-bold text-[#2A2A2A] mb-2 block flex items-center gap-2">
              <Dog className="w-4 h-4 text-[#FF9800]" />
              Huisdieren toegestaan
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setListing(prev => ({ ...prev, petsAllowed: true }))}
                className={`p-3 rounded-xl border-2 text-center transition-all ${
                  listing.petsAllowed === true
                    ? 'border-[#2E7D32] bg-[#2E7D32]/5'
                    : 'border-[#E5E5E5] bg-white'
                }`}
              >
                <span className={`font-medium ${listing.petsAllowed === true ? 'text-[#2E7D32]' : 'text-[#2A2A2A]'}`}>
                  ✓ Ja
                </span>
              </button>
              <button
                onClick={() => setListing(prev => ({ ...prev, petsAllowed: false }))}
                className={`p-3 rounded-xl border-2 text-center transition-all ${
                  listing.petsAllowed === false
                    ? 'border-[#C04A4A] bg-[#C04A4A]/5'
                    : 'border-[#E5E5E5] bg-white'
                }`}
              >
                <span className={`font-medium ${listing.petsAllowed === false ? 'text-[#C04A4A]' : 'text-[#2A2A2A]'}`}>
                  ✗ Nee
                </span>
              </button>
            </div>
          </div>

          {/* Registration Allowed */}
          <div className="mb-4">
            <label className="text-sm font-bold text-[#2A2A2A] mb-2 block flex items-center gap-2">
              <FileCheck className="w-4 h-4 text-[#1F3A5F]" />
              Inschrijving op adres mogelijk
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setListing(prev => ({ ...prev, registrationAllowed: true }))}
                className={`p-3 rounded-xl border-2 text-center transition-all ${
                  listing.registrationAllowed === true
                    ? 'border-[#2E7D32] bg-[#2E7D32]/5'
                    : 'border-[#E5E5E5] bg-white'
                }`}
              >
                <span className={`font-medium ${listing.registrationAllowed === true ? 'text-[#2E7D32]' : 'text-[#2A2A2A]'}`}>
                  ✓ Ja
                </span>
              </button>
              <button
                onClick={() => setListing(prev => ({ ...prev, registrationAllowed: false }))}
                className={`p-3 rounded-xl border-2 text-center transition-all ${
                  listing.registrationAllowed === false
                    ? 'border-[#C04A4A] bg-[#C04A4A]/5'
                    : 'border-[#E5E5E5] bg-white'
                }`}
              >
                <span className={`font-medium ${listing.registrationAllowed === false ? 'text-[#C04A4A]' : 'text-[#2A2A2A]'}`}>
                  ✗ Nee
                </span>
              </button>
            </div>
          </div>

          {/* Minimum Rental Duration */}
          <div>
            <label className="text-sm font-bold text-[#2A2A2A] mb-2 block flex items-center gap-2">
              <Clock className="w-4 h-4 text-[#9C27B0]" />
              Minimale huurtermijn
            </label>
            <div className="grid grid-cols-2 gap-2">
              {RENTAL_DURATIONS.map((duration) => (
                <button
                  key={duration.id}
                  onClick={() => setListing(prev => ({ ...prev, minRentalDuration: duration.id }))}
                  className={`p-3 rounded-xl border-2 text-center transition-all ${
                    listing.minRentalDuration === duration.id
                      ? 'border-[#9C27B0] bg-[#9C27B0]/5'
                      : 'border-[#E5E5E5] bg-white'
                  }`}
                >
                  <span className={`font-medium text-sm ${
                    listing.minRentalDuration === duration.id ? 'text-[#9C27B0]' : 'text-[#2A2A2A]'
                  }`}>
                    {duration.label}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Property Features Section */}
        <div className="mb-6 p-4 bg-[#F7F7F7] rounded-2xl">
          <h3 className="text-lg font-bold text-[#2A2A2A] mb-4">🏡 Woning Kenmerken</h3>
          
          {/* Outdoor */}
          <div className="mb-4">
            <p className="text-sm font-semibold text-[#666] mb-2">Buiten</p>
            <div className="grid grid-cols-3 gap-2">
              <button onClick={() => setPropertyFeatures(p => ({...p, balcony: !p.balcony}))} className={`p-2 rounded-lg border-2 text-xs ${propertyFeatures.balcony ? 'border-[#2E7D32] bg-[#2E7D32]/10' : 'border-[#E5E5E5]'}`}>
                {propertyFeatures.balcony ? '✓ ' : ''}Balkon
              </button>
              <button onClick={() => setPropertyFeatures(p => ({...p, garden: !p.garden}))} className={`p-2 rounded-lg border-2 text-xs ${propertyFeatures.garden ? 'border-[#2E7D32] bg-[#2E7D32]/10' : 'border-[#E5E5E5]'}`}>
                {propertyFeatures.garden ? '✓ ' : ''}Tuin
              </button>
              <button onClick={() => setPropertyFeatures(p => ({...p, storage: !p.storage}))} className={`p-2 rounded-lg border-2 text-xs ${propertyFeatures.storage ? 'border-[#2E7D32] bg-[#2E7D32]/10' : 'border-[#E5E5E5]'}`}>
                {propertyFeatures.storage ? '✓ ' : ''}Berging
              </button>
            </div>
          </div>

          {/* Shared/Private */}
          <div className="mb-4">
            <p className="text-sm font-semibold text-[#666] mb-2">Gedeeld/Eigen</p>
            <div className="grid grid-cols-2 gap-2">
              {['kitchen', 'shower', 'toilet', 'livingroom'].map(k => (
                <div key={k} className="flex items-center justify-between bg-white p-2 rounded-lg">
                  <span className="text-xs capitalize">{k === 'kitchen' ? 'Keuken' : k === 'shower' ? 'Douche' : k === 'toilet' ? 'Toilet' : 'Woonkamer'}</span>
                  <select value={propertyFeatures[k]} onChange={(e) => setPropertyFeatures(p => ({...p, [k]: e.target.value}))} className="text-xs border rounded p-1">
                    <option value="">-</option>
                    <option value="eigen">Eigen</option>
                    <option value="gedeeld">Gedeeld</option>
                  </select>
                </div>
              ))}
            </div>
          </div>

          {/* Amenities */}
          <div className="mb-4">
            <p className="text-sm font-semibold text-[#666] mb-2">Voorzieningen</p>
            <div className="grid grid-cols-3 gap-2">
              {[
                {k: 'elevator', l: 'Lift'}, {k: 'washingConnection', l: 'Wasmachine'}, {k: 'dryerConnection', l: 'Droger'},
                {k: 'dishwasher', l: 'Vaatwasser'}, {k: 'internetIncluded', l: 'Internet'}, {k: 'smokingAllowed', l: 'Roken OK'},
                {k: 'airco', l: 'Airco'}, {k: 'bikeStorage', l: 'Fietsenstalling'}, {k: 'solarPanels', l: 'Zonnepanelen'}
              ].map(({k, l}) => (
                <button key={k} onClick={() => setPropertyFeatures(p => ({...p, [k]: !p[k]}))} className={`p-2 rounded-lg border-2 text-xs ${propertyFeatures[k] ? 'border-[#2E7D32] bg-[#2E7D32]/10' : 'border-[#E5E5E5]'}`}>
                  {propertyFeatures[k] ? '✓ ' : ''}{l}
                </button>
              ))}
            </div>
          </div>

          {/* Bathroom */}
          <div className="mb-4">
            <p className="text-sm font-semibold text-[#666] mb-2">Badkamer</p>
            <div className="grid grid-cols-4 gap-2">
              {[{k: 'bathtub', l: 'Bad'}, {k: 'showerBathroom', l: 'Douche'}, {k: 'sink', l: 'Wastafel'}, {k: 'toiletInBathroom', l: 'Toilet'}].map(({k, l}) => (
                <button key={k} onClick={() => setPropertyFeatures(p => ({...p, [k]: !p[k]}))} className={`p-2 rounded-lg border-2 text-xs ${propertyFeatures[k] ? 'border-[#2E7D32] bg-[#2E7D32]/10' : 'border-[#E5E5E5]'}`}>
                  {propertyFeatures[k] ? '✓ ' : ''}{l}
                </button>
              ))}
            </div>
          </div>

          {/* Parking */}
          <div className="mb-4">
            <p className="text-sm font-semibold text-[#666] mb-2">Parkeren</p>
            <div className="grid grid-cols-2 gap-2">
              {[{k: 'parkingIncluded', l: 'Inclusief'}, {k: 'parkingAvailable', l: 'Beschikbaar (betaald)'}, {k: 'parkingPermitNeeded', l: 'Vergunning nodig'}, {k: 'noParking', l: 'Geen parkeren'}].map(({k, l}) => (
                <button key={k} onClick={() => setPropertyFeatures(p => ({...p, [k]: !p[k]}))} className={`p-2 rounded-lg border-2 text-xs ${propertyFeatures[k] ? 'border-[#2E7D32] bg-[#2E7D32]/10' : 'border-[#E5E5E5]'}`}>
                  {propertyFeatures[k] ? '✓ ' : ''}{l}
                </button>
              ))}
            </div>
          </div>

          {/* Location */}
          <div className="mb-4">
            <p className="text-sm font-semibold text-[#666] mb-2">Locatie</p>
            <div className="grid grid-cols-3 gap-2">
              {[{k: 'publicTransport', l: 'OV dichtbij'}, {k: 'supermarketNearby', l: 'Supermarkt'}, {k: 'schoolNearby', l: 'School'}].map(({k, l}) => (
                <button key={k} onClick={() => setPropertyFeatures(p => ({...p, [k]: !p[k]}))} className={`p-2 rounded-lg border-2 text-xs ${propertyFeatures[k] ? 'border-[#2E7D32] bg-[#2E7D32]/10' : 'border-[#E5E5E5]'}`}>
                  {propertyFeatures[k] ? '✓ ' : ''}{l}
                </button>
              ))}
            </div>
          </div>

          {/* Energy Label & Neighborhood */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-[#666] mb-1 block">Energielabel</label>
              <select value={propertyFeatures.energyLabel} onChange={(e) => setPropertyFeatures(p => ({...p, energyLabel: e.target.value}))} className="w-full p-2 rounded-lg border-2 border-[#E5E5E5] text-sm">
                <option value="">-</option>
                {['A++++','A+++','A++','A+','A','B','C','D','E','F','G'].map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#666] mb-1 block">Buurt type</label>
              <select value={propertyFeatures.neighborhoodType} onChange={(e) => setPropertyFeatures(p => ({...p, neighborhoodType: e.target.value}))} className="w-full p-2 rounded-lg border-2 border-[#E5E5E5] text-sm">
                <option value="">-</option>
                <option value="rustig">Rustig</option>
                <option value="levendig">Levendig</option>
                <option value="centrum">Centrum</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#E5E5E5] p-4">
        <div className="max-w-xl mx-auto">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full h-14 bg-[#2E7D32] hover:bg-[#256B2B] text-white rounded-xl font-bold text-lg"
          >
            {saving ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Save className="w-5 h-5 mr-2" />
                Wijzigingen opslaan
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EditListingPage;
